import type {
    DisclosurePlayback,
    SalesAnalytics,
    SalesContextValue,
    SalesFeatures,
    SalesTimeline,
} from "@expert/sales-tools";
import type { AudioType } from "@expert/shared-types";

import {
    addGenericMessage,
    addMessage,
    addSessionSummaryMessage,
    deactivatedSummaryMessage,
    deactivatePreviousSessionSummaryMessage,
    useTimelineStore,
} from "@asurion-core/expert-workspace-timeline";
import { useCallIntroFlowQuestions, useCustomerAvatar } from "@expert/contentful";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useGaiaWebsocket } from "@expert/gaia";
import { trackFullStoryEvent } from "@expert/monitoring";
import { DisclosurePlayError } from "@expert/sales-tools";
import { useEffect, useMemo, useState } from "react";

import { getRootDispatcher } from "@/analytics";
import { onC360CustomerConfirmed, useC360InteractionIds } from "@/c360";
import { getDisclosureEndpointArn } from "@/components/workspace/hooks/getDisclosureEndpointArn";
import { env } from "@/config";
import { useIVRInfo } from "@/customer-info/api";
import { useTaskMdn } from "@/customer-panel/hooks/useTaskMdn";
import { selectFlowsTab, setSelectedTab } from "@/panels/modules/panels/store/panels.store.selectors";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import {
    isChatTask,
    isSimulatedVoiceTask,
    isVoiceTask,
    sdkEventBus,
    useActiveTask,
    useAgentSdk,
    useAgentStore,
    useLogger,
    usePartner,
    useSession,
} from "@/sdk";
import { useAgentAttributesStore } from "@/sdk/agent/store/agent-attributes.store";
import { DisclosureContactNotReadyError, DisclosureInFlightError } from "@/sdk/errorTypes";
import { getSharedApiClient, useFeatureFlag, userCache } from "@/shared-utils";
import { sidecarTabEventBus } from "@/sidecar/sidecarTabEventBus";
import { endDynatraceAction, startDynatraceAction } from "@/utils/RealUserMonitoringClient";

// ─── Public API ─────────────────────────────────────────────────────────────────

export interface UseBuildSalesContextParams {
    /**
     * Gates redesign-only behaviors (e.g. the customer-edit tab switch) so the legacy
     * layout, which shares this same context, is unaffected.
     */
    isVoiceWorkspaceRedesignEnabled: boolean;
}

/**
 * Builds a SalesContextValue from expert-ui's existing hooks and utilities.
 * Always returns a value so the WidgetContainer (rendered outside a customer
 * session) can consume context. Session/task fields are filled with safe
 * defaults when no active customer task exists.
 */
export function useBuildSalesContext({
    isVoiceWorkspaceRedesignEnabled,
}: UseBuildSalesContextParams): SalesContextValue {
    const session = useSession();
    const activeTask = useActiveTask();
    const partner = usePartner();
    const partnerConfig = usePartnerConfig();
    const logger = useLogger({ module: "useBuildSalesContext" });
    const agentId = useAgentStore((state) => state.agentId);
    const { features: workspaceFeatures } = useFeatures<WorkspaceFeatures>();
    const gaiaWs = useGaiaWebsocket();
    const agentSdk = useAgentSdk();

    const sessionId = session.id;
    const isVoice = isVoiceTask(activeTask);
    const isChat = isChatTask(activeTask);

    const customerAvatarUrl = useCustomerAvatar(sessionId);
    const callIntroQuestions = useCallIntroFlowQuestions(partner, { enabled: isVoice });
    const ivrInfo = useIVRInfo();
    const c360Interaction = useC360InteractionIds(sessionId);
    const taskMdn = useTaskMdn({
        blocklistedNumbers: partnerConfig.blocklistedNumbers,
        blocklistedPrefixes: partnerConfig.blocklistedPrefixes,
    });

    const chatTaskForFeatureFlag = isChat ? activeTask : undefined;
    const { data: isChatDeviceDetectionEnabled = false } = useFeatureFlag<boolean>(
        "EWP-chat-isDeviceDetectionEnabled",
        {
            partner: chatTaskForFeatureFlag?.partner,
            topic: chatTaskForFeatureFlag?.topic,
            language: chatTaskForFeatureFlag?.language,
        },
        { isEnabled: isChat },
    );
    const [disclosurePlayback, setDisclosurePlayback] = useState<DisclosurePlayback | null>(null);
    useEffect(() => {
        const unsubStart = sdkEventBus.on("disclosure_playback_started", () => {
            setDisclosurePlayback({ state: "playing", startedAt: Date.now() });
        });
        const unsubEnd = sdkEventBus.on("disclosure_playback_ended", () => {
            setDisclosurePlayback(null);
        });
        // Cancel any in-flight disclosure when the call ends — the diff-based detection inside
        // subscribeToContactEvents may not fire after the contact terminates, so we also clear
        // playback state on hangup directly.
        const unsubCallDisconnected = sdkEventBus.on("call_disconnected", (task) => {
            setDisclosurePlayback((current) => {
                if (current) {
                    logger.debug(
                        { taskId: task?.id, reason: "call_disconnected" },
                        "clearing in-flight disclosure playback state",
                    );
                }
                return null;
            });
        });
        // Only clear on THIS session's own end; a concurrent chat ending must not wipe
        // the visible session's in-flight playback (connectionState/events are global).
        const unsubSession = sdkEventBus.on("session_changed", ({ action, session: changedSession }) => {
            if (action === "end" && changedSession.id === sessionId) setDisclosurePlayback(null);
        });
        return () => {
            unsubStart();
            unsubEnd();
            unsubCallDisconnected();
            unsubSession();
        };
    }, [logger, sessionId]);

    // Stabilize the three builder outputs so they keep a constant identity across
    // re-renders. Previously these were constructed inline inside the big memo
    // below, so every recompute (driven by ~19 deps) minted brand-new
    // apiClients/analytics/timeline objects — re-firing any consumer effect/query
    // that depended on their identity (e.g. ProductSaleFlow's "PageShown" dispatch
    // and useEligibilityPrecheck's 10s timer). See #6300 / #6265.
    const apiClients = useMemo(() => buildApiClients(sessionId), [sessionId]);
    const analytics = useMemo(() => buildAnalytics(), []);
    const timeline = useMemo(() => buildTimeline(), []);

    // Memoized on the voice partner alone so the record keeps a stable identity across the ~20-dep
    // context memo below (see Referential Stability guidance) — consumers can depend on it safely.
    const voiceTaskPartner = !!activeTask && isVoice ? activeTask.partner : undefined;
    const disclosureAudioAvailability = useMemo(
        () => buildDisclosureAudioAvailability(voiceTaskPartner),
        [voiceTaskPartner],
    );

    // Memoized on its actual inputs (not the ~19 deps below) so the reference only changes when a
    // flag value actually changes — lets the logging effect below fire only on real changes instead
    // of every context recompute (e.g. every hold/mute/call-state change re-mints `activeTask`).
    const features = useMemo(
        () => buildFeatures(partnerConfig.features, workspaceFeatures, { isChatDeviceDetectionEnabled }),
        [partnerConfig.features, workspaceFeatures, isChatDeviceDetectionEnabled],
    );
    useEffect(() => {
        logger.info({ features }, "Sales features resolved");
    }, [features, logger]);

    return useMemo<SalesContextValue>(() => {
        // Match legacy CustomerPanel parity: legacy uses useTypedMostRecentTask which
        // returns the active task in training mode (Expert University) where session.kind
        // is not "with-customer". Gating on isCustomerSession here caused the decoupled
        // CustomerPanel to bail out in training. Follow the task itself instead.
        const hasSession = !!activeTask;
        const voiceTask = hasSession && isVoice ? activeTask : null;
        const chatTask = hasSession && isChat ? activeTask : null;

        return {
            sessionId,
            partner,
            partnerConfig,
            taskType: { isVoice: hasSession && isVoice, isChat: hasSession && isChat },
            taskData: {
                // The C360 line is the session's, open until the session ends, so it outlives a call that ends
                // mid-session (a customer hangup, or before a callback). Dropping it with the task changed
                // useEligibility's key, and the refetch sent USP the fallback interaction ID against the stored line ID.
                c360Interaction,
                ...(hasSession && {
                    customerInteractionId: activeTask.sessionId,
                    asurionCallId: voiceTask?.asurionCallId,
                    agentCallId: voiceTask?.agentCallId,
                    reservationSid: undefined, // TODO: Is this okay?
                    chatSourceApp: chatTask?.sourceApp,
                    chatTopic: chatTask?.topic,
                    chatLanguage: chatTask?.language,
                    locale: activeTask.locale,
                    mdn: taskMdn || undefined,
                    salesChannel: activeTask.type,
                }),
            },
            expertLocation: useAgentAttributesStore.getState().site,
            agentId,
            employeeId: userCache.employeeId ?? "",
            pilots: userCache.pilots ?? [],
            isSimulatedVoiceTask: hasSession ? isSimulatedVoiceTask(activeTask) : false,
            features,
            activeChatId: chatTask?.chatId,
            ivrInfo: hasSession && isVoice ? ivrInfo : undefined,
            callIntroQuestions,
            logger,
            apiClients,
            analytics,
            gaiaWebsocket: gaiaWs ? { sendJsonMessage: gaiaWs.sendJsonMessage, loading: gaiaWs.loading } : undefined,
            timeline,
            customerAvatarUrl,
            startedTimestamp: session.startedTimestamp,
            onQuickRebuttal: (text) => {
                addGenericMessage(sessionId, text);
                setSelectedTab("rightBottom")("a"); // legacy layout: focus the Expert Assist panel
                sidecarTabEventBus.emit("focus-tab", "ai-assist"); // redesigned layout: focus AI Assist tab
            },
            onPlayDisclosure: async (audioType: AudioType) => {
                if (!voiceTask) return;
                // Conference the disclosure by dialing its configured ARN directly, so playback does
                // not depend on the Quick Connect being in the agent's routing profile. Availability
                // (disclosureAudioAvailability) gates rendering on the same config resolver, so a
                // missing ARN never reaches here.
                const endpointArn = getDisclosureEndpointArn(voiceTask.partner, audioType);
                if (!endpointArn) return;
                try {
                    await agentSdk.startConferenceWithEndpointArn(voiceTask, endpointArn);
                } catch (error) {
                    // Translate the SDK's provider-specific typed errors into the sales-tools UI
                    // contract (DisclosurePlayError + reason) at this boundary, so the UI never
                    // imports SDK error types or parses error strings.
                    if (error instanceof DisclosureContactNotReadyError) {
                        throw new DisclosurePlayError("contact_not_ready", { cause: error });
                    }
                    if (error instanceof DisclosureInFlightError) {
                        throw new DisclosurePlayError("in_flight", { cause: error });
                    }
                    throw new DisclosurePlayError("conference_error", { cause: error });
                }
            },
            // Per-language availability: each AudioType is available when a disclosure Quick Connect
            // ARN is configured for this partner + language. This is independent of the agent's
            // routing profile — the conference dials the ARN directly. The consumer reads the entry
            // for the expert's selected language; if the conference fails at play time, the flow
            // degrades to the Continue button (handled in RecordedLegalDisclosuresSection /
            // FullOfferChecklistStep). Memoized above for referential stability.
            disclosureAudioAvailability,
            disclosurePlayback,
            // Compliance guard: disables the disclosure Play button while the customer is on hold, so
            // a required disclosure can't be played to a held (non-hearing) customer. Reactive via the
            // immerable session store (holdState changes mint a new activeTask identity).
            isCustomerOnHold: voiceTask ? voiceTask.holdState.onHold : false,
            onProductSelected: () => {
                selectFlowsTab();
            },
            // Links the call's Customer 360 line to the customer the expert confirmed.
            onCustomerConfirmed: (sessionId, customer) => {
                void onC360CustomerConfirmed(sessionId, customer);
            },
            // Redesign-only: the legacy layout shows the edit form inline (no tab to switch to), so
            // leave this unset there to keep legacy behavior untouched.
            onEditCustomerInfo: isVoiceWorkspaceRedesignEnabled
                ? () => {
                      sidecarTabEventBus.emit("focus-tab", "customer");
                  }
                : undefined,
            // RDESQ-1988: push Discovery/Engagement (and other) suggested prompts into the
            // Expert Assist timeline. Defaults to SalesGuidance (sales-owned bubble with title + body).
            onSendPromptToTimeline: ({ text, type = "SalesGuidance", title, items }) => {
                const metadata =
                    type === "SalesMessage" && title
                        ? { salesMessage: { title, text } }
                        : title
                          ? { salesGuidance: { title, items: items ?? [] } }
                          : undefined;
                addMessage(sessionId, {
                    id: crypto.randomUUID(),
                    text,
                    type,
                    isUnread: true,
                    status: "active",
                    key: "intentDetected",
                    metadata,
                });
            },
        };
    }, [
        activeTask,
        sessionId,
        partner,
        partnerConfig,
        isVoice,
        isChat,
        agentId,
        features,
        callIntroQuestions,
        logger,
        gaiaWs,
        customerAvatarUrl,
        session.startedTimestamp,
        agentSdk,
        disclosurePlayback,
        ivrInfo,
        taskMdn,
        c360Interaction,
        apiClients,
        analytics,
        timeline,
        disclosureAudioAvailability,
        isVoiceWorkspaceRedesignEnabled,
    ]);
}

// ─── Disclosure audio availability ────────────────────────────────────────────────

/**
 * Builds the per-language availability record consumed by the sales-tools disclosure flow. Each
 * AudioType is available when a disclosure endpoint ARN is configured for the partner (see
 * {@link getDisclosureEndpointArn}) — a synchronous config lookup, independent of the agent's
 * routing profile. Returns all-false when there is no active voice task.
 */
function buildDisclosureAudioAvailability(partner: string | undefined): Record<AudioType, boolean> {
    const isAvailable = (audioType: AudioType) =>
        partner !== undefined && getDisclosureEndpointArn(partner, audioType) !== undefined;
    return {
        PreRecordedDisclosures: isAvailable("PreRecordedDisclosures"),
        PreRecordedDisclosuresSpanish: isAvailable("PreRecordedDisclosuresSpanish"),
    };
}

// ─── Feature flags ──────────────────────────────────────────────────────────────

function buildFeatures(
    partnerFeatures: ReturnType<typeof usePartnerConfig>["features"],
    workspaceFeatures: WorkspaceFeatures,
    dynamicFeatures: { isChatDeviceDetectionEnabled: boolean },
): SalesFeatures {
    return {
        isChallengesEnabled: partnerFeatures.isChallengesEnabled ?? false,
        isExpertPerformanceDashboardEnabled: partnerFeatures.isExpertPerformanceDashboardEnabled ?? false,
        isSalesQuestionnaireEnabled: partnerFeatures.isSalesQuestionnaireEnabled ?? false,
        isRealTimeComplianceCheckEnabled: partnerFeatures.isRealTimeComplianceCheckEnabled ?? false,
        isDigitalCompetencyInsightEnabled: partnerFeatures.isDigitalCompetencyInsightEnabled ?? false,
        isChallengeEnabled: workspaceFeatures.isChallengeEnabled,
        isChecklistSummaryEnabled: workspaceFeatures.isChecklistSummaryEnabled,
        isPreviousSessionSummaryEnabled: workspaceFeatures.isPreviousSessionSummaryEnabled ?? false,
        isRapportEnabled: workspaceFeatures.isRapportEnabled,
        isPacingEnabled: workspaceFeatures.isPacingEnabled,
        isUsingMovedDiscoveryAndEngagementQuestions:
            workspaceFeatures.isUsingMovedDiscoveryAndEngagementQuestions ?? false,
        isVerizonCareEnabled: workspaceFeatures.isVerizonCareEnabled ?? false,
        isChatDeviceDetectionEnabled: dynamicFeatures.isChatDeviceDetectionEnabled,
        isAudioDisclosuresEnabled: workspaceFeatures.isAudioDisclosuresEnabled,
        isUpdatedCustomerHandoffEnabled: workspaceFeatures.isUpdatedCustomerHandoffEnabled,
        isGuidedBrowsingEnabled: workspaceFeatures.isGuidedBrowsingEnabled ?? false,
        isLongFormAiEnabled: workspaceFeatures.isLongFormAiEnabled ?? false,
        isStringExamplesListEnabled: workspaceFeatures.isStringExamplesListEnabled ?? false,
        isClaimDetailsEnabled: workspaceFeatures.isClaimDetailsEnabled ?? false,
    };
}

// ─── API clients ────────────────────────────────────────────────────────────────

function buildApiClients(sessionId: string): SalesContextValue["apiClients"] {
    const baseClient = getSharedApiClient(sessionId);
    const apiBaseUrl = env.backendUrls.apiBaseUrl;
    return {
        sales: baseClient.extend(() => ({ prefix: `${apiBaseUrl}/api/sales` })),
        gamification: baseClient.extend(() => ({
            prefix: `${apiBaseUrl}/api/gamification`,
            retry: { limit: 0 },
        })),
        customer: baseClient.extend(() => ({ prefix: `${apiBaseUrl}/api/customer` })),
    };
}

// ─── Analytics adapter ──────────────────────────────────────────────────────────

// Resolve the root dispatcher lazily (per call) rather than capturing it by
// value. buildAnalytics() is now memoized with a stable identity, so a captured
// dispatcher would be frozen to whatever it was at first build (pre-login),
// silently dropping later runtime transforms — partner binding
// (updateDispatcherPartner) and test-user tagging (setIdentityAsTestUser).
// Resolving lazily keeps the adapter stable AND always current.
function buildDispatcherAdapter(
    getDispatcher: () => ReturnType<typeof getRootDispatcher>,
): SalesAnalytics["dispatcher"] {
    return {
        dispatchBusinessEvent: (name, data) =>
            getDispatcher()
                .withExtra(data ?? {})
                .dispatchBusinessEvent(name),
        dispatchAction: (name, detail, data) =>
            getDispatcher()
                .withExtra(data ?? {})
                .dispatchAction(name, detail),
        dispatchUserAction: (name, detail, data) =>
            getDispatcher()
                .withExtra(data ?? {})
                .dispatchUserAction(name, detail),
        withExtra: (data) => buildDispatcherAdapter(() => getDispatcher().withExtra(data)),
        withMeta: (data) => buildDispatcherAdapter(() => getDispatcher().withMeta(data)),
        withIdentity: (key, value) => buildDispatcherAdapter(() => getDispatcher().withIdentity(key, value)),
        withIdentities: (identities) => buildDispatcherAdapter(() => getDispatcher().withIdentities(identities)),
    };
}

function buildAnalytics(): SalesAnalytics {
    return {
        dispatcher: buildDispatcherAdapter(getRootDispatcher),
        trackFullStoryEvent: (name, data) => trackFullStoryEvent(name, data ?? {}),
        sendToHoneycomb: (_sessionId, data) =>
            getRootDispatcher().withExtra(data).dispatchBusinessEvent("HoneycombSalesEvent"),
        startDynatraceAction: (name, id) =>
            startDynatraceAction(name as Parameters<typeof startDynatraceAction>[0], id),
        endDynatraceAction: (name, error) =>
            endDynatraceAction(name as Parameters<typeof endDynatraceAction>[0], error),
    };
}

// ─── Timeline adapter ───────────────────────────────────────────────────────────

function buildTimeline(): SalesTimeline {
    return {
        getMessages: (sessionId) => useTimelineStore.getState().getTimelineState(sessionId)?.messages ?? [],
        addSessionSummaryMessage: (sessionId, data) => {
            addSessionSummaryMessage(sessionId, {
                summary: data.summary,
                header: data.header,
                timestamp: data.timestamp,
                sessionType: data.sessionType,
                resolutionStatus: data.resolutionStatus,
            });
        },
        deactivatePreviousSessionSummaryMessage,
        deactivatedSummaryMessage,
        addMessage,
    };
}
